import MessageCard from '@/components/chat/message-card';
import { Message, MessageDeliveryStatus } from '@/store/useChatStore';
import { Icon } from '@iconify/react';
import { cn } from '@nextui-org/react';
import { cva, type VariantProps } from 'class-variance-authority';
import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { MarkdownMessageContent } from './markdown-message-content';
import { StreamingMessageContent } from './streaming-message-content';

interface VolumeControlProps {
  messageId: string;
  isPlaying: boolean;
  togglePlayPause: (messageId: string) => void;
  enableTTS: boolean;
  isLoading: boolean;
}

function VolumeControl({
  messageId,
  isPlaying,
  togglePlayPause,
  enableTTS,
  isLoading,
}: VolumeControlProps) {
  if (!enableTTS) return null;

  return (
    <Icon
      onClick={() => togglePlayPause(messageId)}
      icon={
        isLoading
          ? 'mdi:loading'
          : isPlaying
            ? 'mdi:pause'
            : 'fluent:speaker-2-16-filled'
      }
      className={`text-gray-500 cursor-pointer ${isLoading ? 'animate-spin' : 'hover:text-gray-700'}`}
      width={20}
    />
  );
}

const formatMessageTime = (timestamp: string) => {
  const messageDate = new Date(timestamp);
  // Format as HH:MM (24-hour format)
  return messageDate.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const messageVariants = cva(
  'px-4 py-3 rounded-2xl shadow-sm', // base styles with rounded corners
  {
    variants: {
      variant: {
        user: 'bg-[#5B77B6] text-white rounded-tr-sm',
        assistant:
          'bg-[#F5F5F5] dark:bg-gray-600 text-gray-800 dark:text-gray-100 rounded-tl-sm',
        // Add more variants as needed
        success: 'bg-green-500 text-white',
        warning: 'bg-yellow-500 text-white',
        error: 'bg-red-500 text-white',
      },
    },
    defaultVariants: {
      variant: 'assistant',
    },
  }
);

type MessageVariant = 'user' | 'assistant' | 'success' | 'warning' | 'error';

interface MessageListProps extends VariantProps<typeof messageVariants> {
  messages: Message[];
  isThinking: boolean;
  playingMessageId: string | null;
  togglePlayPause: (id: string, text: string) => void;
  enableTTS: boolean;
  loadingMessageId: string | null;
  userMessageVariant?: MessageVariant;
  assistantMessageVariant?: MessageVariant;
  streamingMessageId?: string | null;
  streamingTargetText?: string;
  onRetryMessage?: (messageId: string) => void;
  onStreamingFirstDisplay?: () => void;
  /** Extra content rendered inside one assistant message, below its text. */
  messageAttachment?: MessageAttachment | null;
}

export interface MessageAttachment {
  messageId: string;
  content: React.ReactNode;
}

const MessageComponent = memo(
  ({
    messageId,
    messageText,
    messageTimestamp,
    isUser,
    isStreaming,
    isPlaying,
    isLoading,
    userMessageVariant,
    assistantMessageVariant,
    enableTTS,
    togglePlayPause,
    deliveryStatus,
    deliveryError,
    onRetryMessage,
    streamingTargetText,
    onStreamingDisplayUpdate,
    onStreamingFirstDisplay,
    attachment,
  }: {
    messageId: string;
    messageText: string;
    messageTimestamp: string;
    isUser: boolean;
    isStreaming: boolean;
    isPlaying: boolean;
    isLoading: boolean;
    enableTTS: boolean;
    userMessageVariant: MessageVariant;
    assistantMessageVariant: MessageVariant;
    togglePlayPause: (id: string, text: string) => void;
    deliveryStatus?: MessageDeliveryStatus;
    deliveryError?: string;
    onRetryMessage?: (messageId: string) => void;
    streamingTargetText?: string;
    onStreamingDisplayUpdate?: () => void;
    onStreamingFirstDisplay?: () => void;
    attachment?: React.ReactNode;
  }) => {
    if (isStreaming && !isUser && !streamingTargetText?.trim()) {
      return null;
    }

    const volumeControl = !isUser && (
      <VolumeControl
        messageId={messageId}
        isPlaying={isPlaying}
        togglePlayPause={(id) => togglePlayPause(id, messageText)}
        enableTTS={enableTTS}
        isLoading={isLoading}
      />
    );

    return (
      <MessageCard
        className={isUser ? 'flex-row-reverse' : 'flex-row'}
        avatar={!isUser ? 'Lift' : 'Me'}
        message={
          <>
            {isStreaming && !isUser ? (
              <StreamingMessageContent
                targetText={streamingTargetText ?? messageText}
                isActive={isStreaming}
                onDisplayUpdate={onStreamingDisplayUpdate}
                onFirstDisplay={onStreamingFirstDisplay}
              />
            ) : (
              <MarkdownMessageContent text={messageText} isUser={isUser} />
            )}
            {!isUser && !isStreaming && attachment}
            {isStreaming && (
              <span className="inline-block w-2 h-4 bg-gray-400 ml-1 animate-pulse"></span>
            )}
            {isUser && deliveryStatus === 'failed' && (
              <div className="mt-2 flex flex-col gap-2">
                <p className="text-xs text-white">
                  {deliveryError || 'Message not delivered'}
                </p>
                {onRetryMessage && (
                  <button
                    type="button"
                    onClick={() => onRetryMessage(messageId)}
                    className="self-start rounded-full bg-white px-3 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-50"
                  >
                    Retry
                  </button>
                )}
              </div>
            )}
            {isUser && deliveryStatus === 'interrupted' && (
              <div className="mt-2 flex flex-col gap-2">
                <p className="text-xs text-white">
                  {deliveryError || 'The reply to this message did not finish.'}
                </p>
                {onRetryMessage && (
                  <button
                    type="button"
                    onClick={() => onRetryMessage(messageId)}
                    className="self-start rounded-full bg-white px-3 py-1 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-50"
                  >
                    Retry response
                  </button>
                )}
              </div>
            )}
            <div className="flex items-center justify-end gap-2 ">
              <span className="text-[0.7rem] opacity-70 mt-1">
                {formatMessageTime(messageTimestamp)}
              </span>
              {isUser && deliveryStatus === 'delivering' && (
                <span className="text-[0.7rem] text-white/80">Sending...</span>
              )}
              {volumeControl}
            </div>
          </>
        }
        messageClassName={cn(
          messageVariants({
            variant: isUser ? userMessageVariant : assistantMessageVariant,
          }),
          isUser &&
            (deliveryStatus === 'failed' || deliveryStatus === 'interrupted') &&
            (deliveryStatus === 'failed'
              ? 'border border-red-300 dark:border-red-700'
              : 'border border-amber-300 dark:border-amber-700')
        )}
        isUserMessage={isUser}
      />
    );
  }
);

MessageComponent.displayName = 'MessageComponent';

// Distance (px) from the bottom that still counts as "at the bottom", so
// auto-scroll resumes once the reader scrolls back down to the latest message.
const BOTTOM_THRESHOLD_PX = 48;
// Distance (px) from the bottom after which the "Jump to latest" button shows.
const SCROLL_BUTTON_THRESHOLD_PX = 150;
/** The jump button fades out once the reader has been still this long. */
const SCROLL_BUTTON_IDLE_MS = 2000;

const SCROLL_UP_KEYS = new Set(['ArrowUp', 'PageUp', 'Home']);

function maxScrollTop(element: HTMLElement) {
  return element.scrollHeight - element.clientHeight;
}

function distanceFromBottom(element: HTMLElement) {
  return maxScrollTop(element) - element.scrollTop;
}

function findScrollParent(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement;

  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflowY === 'overlay'
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }

  return null;
}

function MessageList({
  messages,
  isThinking,
  playingMessageId,
  togglePlayPause,
  enableTTS,
  loadingMessageId,
  userMessageVariant = 'user',
  assistantMessageVariant = 'assistant',
  streamingMessageId,
  streamingTargetText = '',
  onRetryMessage,
  onStreamingFirstDisplay,
  messageAttachment,
}: MessageListProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const prevMessagesRef = useRef<Message[]>([]);
  const anchoredMessageIdsRef = useRef<Set<string>>(new Set());
  const lastStreamScrollRef = useRef(0);
  // Whether the view should follow new content. Turned off as soon as the
  // reader scrolls up, and back on when they return to the bottom.
  const autoScrollRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const lastMaxScrollTopRef = useRef(0);
  const prevStreamingMessageIdRef = useRef<string | null>(null);
  // True while a finger or the mouse is held down on the scroll area (touch
  // scrolling, scrollbar dragging); auto-scroll must not fight the reader then.
  const isPointerDownRef = useRef(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isScrollButtonAwake, setIsScrollButtonAwake] = useState(false);
  const scrollButtonIdleTimerRef = useRef<number>();
  const isScrollButtonHoveredRef = useRef(false);

  // Shows the button and restarts its idle countdown. Hovering holds it open,
  // so it cannot fade out from under a pointer heading for it.
  const wakeScrollButton = useCallback(() => {
    setIsScrollButtonAwake(true);
    window.clearTimeout(scrollButtonIdleTimerRef.current);
    if (isScrollButtonHoveredRef.current) return;
    scrollButtonIdleTimerRef.current = window.setTimeout(
      () => setIsScrollButtonAwake(false),
      SCROLL_BUTTON_IDLE_MS
    );
  }, []);

  useEffect(() => {
    // Appearing (e.g. a reply arrives while scrolled up) counts as activity.
    if (showScrollButton) {
      wakeScrollButton();
      return;
    }
    // Hidden by a click, the button leaves from under the cursor without a
    // pointerleave, which would otherwise hold it open on its next appearance.
    isScrollButtonHoveredRef.current = false;
  }, [showScrollButton, wakeScrollButton]);

  // A streamed reply arrives in bursts; waking per chunk made the button blink
  // whenever a pause outlasted the idle delay. Hold it for the whole reply and
  // start the countdown once the reply is done.
  const isReplyStreaming = Boolean(streamingMessageId) && !isThinking;
  const wasReplyStreamingRef = useRef(false);
  useEffect(() => {
    if (wasReplyStreamingRef.current && !isReplyStreaming) wakeScrollButton();
    wasReplyStreamingRef.current = isReplyStreaming;
  }, [isReplyStreaming, wakeScrollButton]);

  useEffect(
    () => () => window.clearTimeout(scrollButtonIdleTimerRef.current),
    []
  );

  // Programmatic scrolls record where they landed, so the scroll handler does
  // not mistake them for the reader scrolling.
  const syncScrollPosition = (scrollContainer: HTMLElement) => {
    lastScrollTopRef.current = scrollContainer.scrollTop;
    lastMaxScrollTopRef.current = maxScrollTop(scrollContainer);
  };

  const updateScrollButton = (scrollContainer: HTMLElement) => {
    setShowScrollButton(
      distanceFromBottom(scrollContainer) > SCROLL_BUTTON_THRESHOLD_PX
    );
  };

  const scrollToBottom = () => {
    autoScrollRef.current = true;
    setShowScrollButton(false);
    const scrollContainer = resolveScrollContainer();

    if (scrollContainer) {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'smooth',
      });
      return;
    }

    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const resolveScrollContainer = () => {
    if (!scrollContainerRef.current && rootRef.current) {
      scrollContainerRef.current =
        findScrollParent(rootRef.current) ?? containerRef.current;
    }

    return scrollContainerRef.current;
  };

  const scrollElementToViewStart = (element: Element) => {
    const scrollContainer = resolveScrollContainer();

    if (!scrollContainer) {
      element.scrollIntoView({ block: 'start', behavior: 'auto' });
      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const topOffset = 8;
    const targetScrollTop =
      scrollContainer.scrollTop +
      (elementRect.top - containerRect.top) -
      topOffset;

    scrollContainer.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: 'auto',
    });
    syncScrollPosition(scrollContainer);
  };

  const scrollToGeneratingArea = () => {
    const generatingArea =
      containerRef.current?.querySelector('[data-generating-area]') ??
      messagesEndRef.current;

    if (generatingArea) {
      scrollElementToViewStart(generatingArea);
    }
  };

  const anchorGeneratingMessage = (messageId: string) => {
    if (anchoredMessageIdsRef.current.has(messageId)) return;

    let attempts = 0;
    const maxAttempts = 12;

    const tryAnchor = () => {
      if (anchoredMessageIdsRef.current.has(messageId)) return;
      // The reader scrolled away while the reply was being prepared; leave
      // them where they are.
      if (!autoScrollRef.current) return;

      resolveScrollContainer();

      const messageEl = containerRef.current?.querySelector(
        `[data-message-id="${messageId}"]`
      );

      if (!messageEl) {
        if (attempts++ < maxAttempts) {
          requestAnimationFrame(tryAnchor);
        }
        return;
      }

      scrollElementToViewStart(messageEl);
      anchoredMessageIdsRef.current.add(messageId);
    };

    tryAnchor();
  };

  const followStreamingMessage = useCallback(() => {
    if (!autoScrollRef.current || isPointerDownRef.current) return;

    const scrollContainer = resolveScrollContainer();

    if (scrollContainer) {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
      syncScrollPosition(scrollContainer);
      return;
    }

    messagesEndRef.current?.scrollIntoView({ block: 'end', behavior: 'auto' });
  }, []);

  useEffect(() => {
    if (!rootRef.current) return;

    scrollContainerRef.current =
      findScrollParent(rootRef.current) ?? containerRef.current;
    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer) return;

    const pauseAutoScroll = () => {
      autoScrollRef.current = false;
    };

    const handleScroll = () => {
      const { scrollTop } = scrollContainer;
      const distance = distanceFromBottom(scrollContainer);
      const currentMaxScrollTop = maxScrollTop(scrollContainer);
      // Content shrinking or the viewport growing (e.g. the input collapsing
      // after a multi-line message is sent) lowers the maximum scroll position
      // and clamps scrollTop up without the reader scrolling.
      const scrollRangeShrank =
        currentMaxScrollTop < lastMaxScrollTopRef.current;

      if (scrollTop < lastScrollTopRef.current && !scrollRangeShrank) {
        // Content growth never moves scrollTop up, so this is the reader.
        // Pause even close to the bottom: a short reply keeps the reader
        // within the threshold, and resuming there would snap them back.
        pauseAutoScroll();
      } else if (
        scrollTop > lastScrollTopRef.current &&
        distance <= BOTTOM_THRESHOLD_PX
      ) {
        // Only resume when the reader scrolls back down to the latest message.
        autoScrollRef.current = true;
      }

      lastScrollTopRef.current = scrollTop;
      lastMaxScrollTopRef.current = currentMaxScrollTop;
      updateScrollButton(scrollContainer);
      wakeScrollButton();
    };

    // Content can grow without the scroll position changing (no scroll event),
    // e.g. when the final reply renders; keep the button in sync with it.
    let lastScrollHeight = scrollContainer.scrollHeight;
    const resizeObserver = new ResizeObserver(() => {
      updateScrollButton(scrollContainer);

      // New content below a reader who has scrolled up (a reply arriving or
      // streaming in) wakes the faded button, as scrolling does.
      const { scrollHeight } = scrollContainer;
      const contentGrew = scrollHeight > lastScrollHeight;
      lastScrollHeight = scrollHeight;
      if (
        contentGrew &&
        distanceFromBottom(scrollContainer) > SCROLL_BUTTON_THRESHOLD_PX
      ) {
        wakeScrollButton();
      }
    });
    resizeObserver.observe(scrollContainer);
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    // Wheel and key events arrive before the scroll they cause, so pausing
    // here stops a streamed chunk from snapping the view back in between.
    const handleWheel = (event: WheelEvent) => {
      if (event.deltaY < 0) pauseAutoScroll();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        SCROLL_UP_KEYS.has(event.key) ||
        (event.key === ' ' && event.shiftKey)
      ) {
        pauseAutoScroll();
      }
    };

    const handlePointerDown = () => {
      isPointerDownRef.current = true;
    };

    const handlePointerUp = () => {
      isPointerDownRef.current = false;
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    scrollContainer.addEventListener('wheel', handleWheel, { passive: true });
    scrollContainer.addEventListener('touchstart', handlePointerDown, {
      passive: true,
    });
    scrollContainer.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('touchend', handlePointerUp);
    window.addEventListener('touchcancel', handlePointerUp);
    window.addEventListener('mouseup', handlePointerUp);

    handleScroll();

    return () => {
      resizeObserver.disconnect();
      scrollContainer.removeEventListener('scroll', handleScroll);
      scrollContainer.removeEventListener('wheel', handleWheel);
      scrollContainer.removeEventListener('touchstart', handlePointerDown);
      scrollContainer.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('touchend', handlePointerUp);
      window.removeEventListener('touchcancel', handlePointerUp);
      window.removeEventListener('mouseup', handlePointerUp);
    };
    // wakeScrollButton is stable, so this still runs once.
  }, [wakeScrollButton]);

  const handleStreamingDisplayUpdate = useCallback(() => {
    if (!streamingMessageId || isThinking) return;

    const now = Date.now();
    if (now - lastStreamScrollRef.current < 80) return;
    lastStreamScrollRef.current = now;
    followStreamingMessage();
  }, [followStreamingMessage, isThinking, streamingMessageId]);

  useLayoutEffect(() => {
    if (!streamingMessageId || isThinking) return;
    anchoredMessageIdsRef.current.delete(streamingMessageId);
    anchorGeneratingMessage(streamingMessageId);
  }, [streamingMessageId, isThinking]);

  useEffect(() => {
    if (!streamingMessageId || isThinking || !streamingTargetText) return;
    followStreamingMessage();
  }, [
    followStreamingMessage,
    isThinking,
    streamingMessageId,
    streamingTargetText,
  ]);

  // When generation finishes the full reply replaces the typewriter text in
  // one render, which can be taller than what was streamed so far; keep a
  // reader who was following the reply at the bottom.
  useEffect(() => {
    const finishedStreaming =
      prevStreamingMessageIdRef.current !== null && !streamingMessageId;
    prevStreamingMessageIdRef.current = streamingMessageId ?? null;

    if (!finishedStreaming) return;
    const frame = requestAnimationFrame(() => followStreamingMessage());
    return () => cancelAnimationFrame(frame);
  }, [streamingMessageId, followStreamingMessage]);

  const attachmentMessageId = messageAttachment?.messageId ?? null;

  // An attachment such as the document card can arrive after the messages
  // render; keep a reader who is already at the bottom of the chat there.
  useEffect(() => {
    if (!attachmentMessageId) return;
    const frame = requestAnimationFrame(() => followStreamingMessage());
    return () => cancelAnimationFrame(frame);
  }, [attachmentMessageId, followStreamingMessage]);

  useEffect(() => {
    if (!isThinking || !autoScrollRef.current) return;
    requestAnimationFrame(() => scrollToGeneratingArea());
  }, [isThinking]);

  useEffect(() => {
    const prevMessages = prevMessagesRef.current;
    const prevIds = new Set(prevMessages.map((message) => message.id));
    const newMessages = messages.filter((message) => !prevIds.has(message.id));

    prevMessagesRef.current = messages;

    if (messages.length < prevMessages.length) {
      anchoredMessageIdsRef.current = new Set(
        [...anchoredMessageIdsRef.current].filter((id) =>
          messages.some((message) => message.id === id)
        )
      );
    }

    if (newMessages.length === 0) return;

    if (newMessages.length > 1) {
      if (messagesEndRef.current) {
        scrollElementToViewStart(messagesEndRef.current);
      }
      newMessages.forEach((message) =>
        anchoredMessageIdsRef.current.add(message.id)
      );
      return;
    }

    const newMessage = newMessages[0];

    if (newMessage.isUser) {
      // Sending a message always brings the reader back to the conversation.
      autoScrollRef.current = true;
      requestAnimationFrame(() => scrollToGeneratingArea());
      return;
    }

    if (!isThinking) {
      anchorGeneratingMessage(newMessage.id);
    }
  }, [messages, isThinking]);

  return (
    <div
      ref={rootRef}
      className="relative flex flex-col space-y-4 md:px-4 h-full"
    >
      <div ref={containerRef} className="flex-1 overflow-y-auto space-y-4">
        {messages.map((message) => (
          <React.Fragment key={message.id}>
            <div data-message-id={message.id}>
              <MessageComponent
                messageId={message.id}
                messageText={message.text}
                messageTimestamp={message.timestamp}
                isUser={message.isUser}
                isStreaming={streamingMessageId === message.id}
                isPlaying={playingMessageId === message.id}
                isLoading={loadingMessageId === message.id}
                userMessageVariant={userMessageVariant}
                assistantMessageVariant={assistantMessageVariant}
                enableTTS={enableTTS}
                togglePlayPause={togglePlayPause}
                deliveryStatus={message.deliveryStatus}
                deliveryError={message.deliveryError}
                onRetryMessage={onRetryMessage}
                attachment={
                  messageAttachment?.messageId === message.id
                    ? messageAttachment.content
                    : undefined
                }
                streamingTargetText={
                  streamingMessageId === message.id
                    ? streamingTargetText
                    : undefined
                }
                onStreamingDisplayUpdate={
                  streamingMessageId === message.id
                    ? handleStreamingDisplayUpdate
                    : undefined
                }
                onStreamingFirstDisplay={
                  streamingMessageId === message.id
                    ? onStreamingFirstDisplay
                    : undefined
                }
              />
            </div>
          </React.Fragment>
        ))}

        {isThinking && (
          <div data-generating-area>
            <MessageCard
              avatar={'Lift'}
              message={
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Thinking</span>
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  </div>
                </div>
              }
              messageClassName="bg-[#F5F5F5] dark:bg-gray-600 rounded-2xl rounded-tl-sm shadow-sm"
            />
          </div>
        )}

        <div ref={messagesEndRef} data-generating-area />
      </div>

      {/* Jump to latest button - sticky so it stays in view while scrolled up */}
      {/* Fades out after SCROLL_BUTTON_IDLE_MS without scrolling, and back in
          on the next scroll. Stays visible while a reply streams in. */}
      {showScrollButton && (
        <div className="sticky bottom-4 h-0 !mt-0 z-10">
          <button
            type="button"
            onClick={scrollToBottom}
            // Mouse only: a tap emulates mouseenter with no mouseleave until
            // the next tap elsewhere, which would hold the button open.
            onPointerEnter={(event) => {
              if (event.pointerType !== 'mouse') return;
              isScrollButtonHoveredRef.current = true;
              wakeScrollButton();
            }}
            onPointerLeave={(event) => {
              if (event.pointerType !== 'mouse') return;
              isScrollButtonHoveredRef.current = false;
              wakeScrollButton();
            }}
            onFocus={wakeScrollButton}
            className={cn(
              'absolute bottom-0 left-1/2 -translate-x-1/2 flex items-center gap-1 whitespace-nowrap bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-full py-2 pl-2 pr-3.5 shadow-lg transition-all duration-300 hover:scale-105',
              isScrollButtonAwake || isReplyStreaming
                ? 'opacity-100'
                : 'opacity-0 pointer-events-none'
            )}
          >
            <Icon icon="mdi:chevron-down" width={20} height={20} />
            Scroll to bottom
          </button>
        </div>
      )}
    </div>
  );
}

export default MessageList;
